// Faint dashed ring at the effective magnet pull radius: shows exactly where
// mote homing kicks in, so manual trawling stays legible (playtest request).
// The radius comes from the sim (world.magnetPullRadius, written every tick
// by game.updatePickups), so Mote Lure ranks, Serenity tier 1 and captain
// traits visibly grow the ring as they scale the pull.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

const SEGMENTS = 64;

export class MagnetRing {
  readonly line: THREE.Line;
  private readonly material: THREE.LineDashedMaterial;

  constructor() {
    // Unit circle; the line scale carries the world-space radius. The first
    // point repeats at the end so plain Line (not LineLoop) line-distances
    // stay uniform and the dash pattern has no closing-segment glitch.
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= SEGMENTS; i++) {
      const a = (i / SEGMENTS) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(a), Math.sin(a), 0));
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    this.material = new THREE.LineDashedMaterial({
      color: 0x3dffc4, // same mote-green as the Mote Lure dish (boon-visuals)
      dashSize: 0.16, // unit-circle space; scales with the ring radius
      gapSize: 0.12,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
    });
    this.line = new THREE.Line(geometry, this.material);
    this.line.computeLineDistances();
    this.line.frustumCulled = false;
  }

  /** Follow the ship (unwrapped camera-anchor position); `radius` is the
   * effective pull radius in arena units. */
  update(pos: Vec2, radius: number, alive: boolean): void {
    const show = alive && radius > 0;
    this.line.visible = show;
    if (!show) return;
    this.line.scale.set(radius, radius, 1);
    // Under the enemy layer (max enemy z ≈ 0.08) so a converging swarm draws
    // over the ring; above the background grid.
    this.line.position.set(pos.x, pos.y, 0.05);
  }

  dispose(): void {
    this.line.geometry.dispose();
    this.material.dispose();
  }
}
